import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/Primitives";
import { toast } from "sonner";
import { Plus, Eye } from "@phosphor-icons/react";

const CLASS_GROUPS = ["Class 9", "Class 10", "Class 11", "Class 12", "B.Com", "B.Sc", "B.CA"];

export default function TeacherAssignments() {
  const [assigns, setAssigns] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", subject_id: "", class_group: "Class 10", due_date: "" });
  const [openId, setOpenId] = useState(null);
  const [subs, setSubs] = useState([]);

  const load = () => api.get("/assignments").then(r => setAssigns(r.data));
  useEffect(() => { load(); api.get("/subjects").then(r => setSubjects(r.data)); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.subject_id) { toast.error("Select subject"); return; }
    try {
      await api.post("/assignments", { ...form, due_date: new Date(form.due_date).toISOString() });
      toast.success("Assignment created");
      setForm({ title: "", description: "", subject_id: "", class_group: "Class 10", due_date: "" });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const viewSubs = async (id) => {
    setOpenId(id);
    const { data } = await api.get(`/submissions/assignment/${id}`);
    setSubs(data);
  };

  const grade = async (sid, val) => {
    await api.post(`/submissions/${sid}/grade`, { grade: val, feedback: "" });
    toast.success("Graded");
    viewSubs(openId);
  };

  return (
    <div>
      <PageHeader eyebrow="Descriptive submissions" title="Assignments" subtitle="Publish assignments, track late submissions and grade." />

      <Section title="New assignment">
        <form onSubmit={create} className="card-academic p-6 grid md:grid-cols-2 gap-4">
          <input placeholder="Title" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="a-title"
            className="bg-ivory border border-stone px-4 py-3 md:col-span-2" />
          <textarea placeholder="Description" required rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="a-desc"
            className="bg-ivory border border-stone px-4 py-3 md:col-span-2" />
          <select value={form.subject_id} onChange={e => setForm({ ...form, subject_id: e.target.value })} data-testid="a-subject"
            className="bg-ivory border border-stone px-4 py-3">
            <option value="">— Subject —</option>
            {subjects.map(s => <option key={s.id} value={s.id}>{s.name} · {s.class_group}</option>)}
          </select>
          <select value={form.class_group} onChange={e => setForm({ ...form, class_group: e.target.value })} data-testid="a-class"
            className="bg-ivory border border-stone px-4 py-3">
            {CLASS_GROUPS.map(c => <option key={c}>{c}</option>)}
          </select>
          <input type="datetime-local" required value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="a-due"
            className="bg-ivory border border-stone px-4 py-3 md:col-span-2" />
          <button className="btn-primary md:col-span-2 flex items-center gap-2 justify-center" data-testid="a-submit"><Plus size={16} /> Publish</button>
        </form>
      </Section>

      <Section title={`Assignments (${assigns.length})`}>
        {assigns.length === 0 ? <EmptyState /> : (
          <div className="space-y-3">
            {assigns.map(a => (
              <div key={a.id} className="card-academic p-5">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="overline">{a.class_group} · due {new Date(a.due_date).toLocaleString()}</div>
                    <h4 className="font-serif text-xl text-forest">{a.title}</h4>
                    <p className="text-sm text-mossy mt-1">{a.description}</p>
                  </div>
                  <button onClick={() => viewSubs(a.id)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`view-subs-${a.id}`}>
                    <Eye size={14} /> Submissions
                  </button>
                </div>

                {openId === a.id && (
                  <div className="mt-4 border-t border-stone pt-4">
                    {subs.length === 0 ? <div className="text-sm text-mossy">No submissions yet.</div> : (
                      <table className="w-full text-sm">
                        <thead><tr>
                          <th className="p-2 overline text-left">Student</th>
                          <th className="p-2 overline text-left">Submitted</th>
                          <th className="p-2 overline text-left">Late</th>
                          <th className="p-2 overline text-left">Content</th>
                          <th className="p-2 overline text-right">Grade</th>
                        </tr></thead>
                        <tbody>
                          {subs.map(s => (
                            <tr key={s.id} className="border-t border-stone">
                              <td className="p-2">{s.student_name}</td>
                              <td className="p-2 font-mono text-xs">{s.submitted_at?.slice(0, 16)}</td>
                              <td className="p-2">{s.is_late ? <span className="text-terracotta">Late</span> : <span className="text-forest">On time</span>}</td>
                              <td className="p-2 max-w-xs truncate">{s.content}</td>
                              <td className="p-2 text-right">
                                <input type="text" placeholder="A+" defaultValue={s.grade || ""}
                                  onBlur={(e) => grade(s.id, e.target.value)} data-testid={`grade-${s.id}`}
                                  className="w-16 bg-ivory border border-stone px-2 py-1 text-right font-mono" />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
